import React, { useState, useEffect } from 'react';
import { query, collection, onSnapshot, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Table,
  TableContainer,
  Paper,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Chip,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

const DAY_MS = 24 * 60 * 60 * 1000;

function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [selectedMenu, setSelectedMenu] = useState('realTimeSummary');

  const [userRoleCounts, setUserRoleCounts] = useState({
    total: 0,
    normal: 0,
    oro: 0,
    moderador: 0,
    platino: 0,
  });
  const [yesterdayCounts, setYesterdayCounts] = useState({
    total: 0,
    normal: 0,
    oro: 0,
    moderador: 0,
    platino: 0,
  });
  const [topUsers, setTopUsers] = useState([]);
  const [topVIPDetails, setTopVIPDetails] = useState([]);
  const [recentNormalUsers, setRecentNormalUsers] = useState([]);
  const [recentReferrals, setRecentReferrals] = useState([]);
  const [systemData, setSystemData] = useState({ fondos: 0, totalFondos: 0 });

  // NEW: historial de inversiones (todos los miembros)
  const [loadingInvestments, setLoadingInvestments] = useState(false);
  const [investments, setInvestments] = useState([]);

  const formatCurrency = (n) =>
    `RD$ ${Number(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}`;

  const formatDate = (msOrDate) => {
    if (!msOrDate) return '-';
    const d = typeof msOrDate === 'number' ? new Date(msOrDate) : msOrDate;
    return d.toLocaleDateString('es-DO'); // simple y claro
  };

  useEffect(() => {
    const fetchSystemData = () => {
      const systemQuery = query(collection(db, 'sistema'));
      const unsubscribeSystem = onSnapshot(systemQuery, (snapshot) => {
        if (!snapshot.empty) {
          const data = snapshot.docs[0].data();
          setSystemData({
            fondos: data.fondos || 0,
            totalFondos: data.totalFondos || 0,
          });
        }
      });
      return () => unsubscribeSystem();
    };
    fetchSystemData();
  }, []);

  useEffect(() => {
    const fetchDashboardData = () => {
      setLoading(true);

      const usersQuery = query(collection(db, 'users'), orderBy('monOro', 'desc'));
      const vipDetailsQuery = query(collection(db, 'vipDetails'), orderBy('Cost', 'desc'));
      const recentNormalUsersQuery = query(
        collection(db, 'users'),
        orderBy('createdAt', 'desc'),
        limit(15)
      );
      const referralsQuery = query(
        collection(db, 'referrals'),
        orderBy('timestamp', 'desc'),
        limit(15)
      );

      const unsubscribeUsers = onSnapshot(usersQuery, (snapshot) => {
        const users = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

        const roles = { total: 0, normal: 0, oro: 0, moderador: 0, platino: 0 };
        users.forEach((user) => {
          const role = user.role?.toLowerCase() || 'normal';
          roles.total++;
          if (roles[role] !== undefined) {
            roles[role]++;
          }
        });

        setUserRoleCounts(roles);

        // Simulación “ayer”
        setYesterdayCounts({
          total: roles.total - 3,
          normal: roles.normal - 2,
          oro: roles.oro + 1,
          moderador: roles.moderador,
          platino: roles.platino - 1,
        });

        setTopUsers(users);
      });

      const unsubscribeVIPDetails = onSnapshot(vipDetailsQuery, (snapshot) =>
        setTopVIPDetails(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })))
      );

      const unsubscribeRecentNormalUsers = onSnapshot(recentNormalUsersQuery, (snapshot) =>
        setRecentNormalUsers(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })))
      );

      const unsubscribeReferrals = onSnapshot(referralsQuery, (snapshot) =>
        setRecentReferrals(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })))
      );

      setLoading(false);

      return () => {
        unsubscribeUsers();
        unsubscribeVIPDetails();
        unsubscribeRecentNormalUsers();
        unsubscribeReferrals();
      };
    };

    fetchDashboardData();
  }, []);

  // NEW: Suscripción al historial de inversiones (solo cuando se abre esa pestaña)
  useEffect(() => {
    if (selectedMenu !== 'investmentHistory') return;

    setLoadingInvestments(true);
    const qInv = query(
      collection(db, 'investments'),
      orderBy('createdAt', 'desc'),
      limit(100)
    );

    const unsub = onSnapshot(
      qInv,
      (snapshot) => {
        const rows = snapshot.docs.map((docSnap) => {
          const d = docSnap.data() || {};
          // Compatibilidad: usa startAtMs/endAtMs si existen; si no, calcula
          const createdAtDate =
            d.createdAt?.toDate?.() ||
            (d.startAtMs ? new Date(d.startAtMs) : null);

          const startMs = d.startAtMs ?? (createdAtDate ? createdAtDate.getTime() : null);
          const endMs =
            d.endAtMs ??
            (startMs && d.periodDays ? startMs + Number(d.periodDays) * DAY_MS : null);

          return {
            id: docSnap.id,
            userId: d.userId || '—',
            lottery: d.lottery || '—',
            amount: Number(d.amount || 0),
            selectedNums: Array.isArray(d.selectedNums) ? d.selectedNums : [],
            periodDays: d.periodDays || 0,
            status: d.status || '—',
            startMs,
            endMs,
          };
        });
        setInvestments(rows);
        setLoadingInvestments(false);
      },
      (err) => {
        console.error('Error cargando historial de inversiones:', err);
        setLoadingInvestments(false);
      }
    );

    return () => unsub();
  }, [selectedMenu]);

  const renderChangeIcon = (current, yesterday) => {
    if (current > yesterday) return <ArrowUpwardIcon color="success" />;
    if (current < yesterday) return <ArrowDownwardIcon color="error" />;
    return null;
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
          <CircularProgress />
        </Box>
      );
    }

    switch (selectedMenu) {
      case 'realTimeSummary':
        return (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ color: '#1976d2', mb: 2 }}>
              Fondos Disponibles
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 3, flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body1">
                  <strong>Fondos:</strong>{' '}
                  {formatCurrency(systemData.fondos)}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body1">
                  <strong>Total Fondos:</strong>{' '}
                  {formatCurrency(systemData.totalFondos)}
                </Typography>
              </Box>
            </Box>
          </Box>
        );

      case 'topUsers':
        return (
          <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Posición</strong></TableCell>
                  <TableCell><strong>Usuario</strong></TableCell>
                  <TableCell><strong>Monedas de Oro</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topUsers
                  .filter((user) => user.monOro >= 1)
                  .map((user, index) => (
                    <TableRow key={user.id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{user.username}</TableCell>
                      <TableCell>{user.monOro}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        );

      case 'vipDetails':
        return (
          <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Posición</strong></TableCell>
                  <TableCell><strong>Usuario</strong></TableCell>
                  <TableCell><strong>Cost</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topVIPDetails.map((vip, index) => (
                  <TableRow key={vip.id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{vip.username}</TableCell>
                    <TableCell>{vip.Cost}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        );

      case 'recentUsers':
        return (
          <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Usuario</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell><strong>Fecha de Registro</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentNormalUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.username || 'Usuario Desconocido'}</TableCell>
                    <TableCell>{user.email || 'Sin Email'}</TableCell>
                    <TableCell>{user.createdAt?.toDate?.().toLocaleDateString?.() || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        );

      case 'recentReferrals':
        return (
          <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Usuario Referido</strong></TableCell>
                  <TableCell><strong>Referido Por</strong></TableCell>
                  <TableCell><strong>Fecha de Registro</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentReferrals.map((referral) => (
                  <TableRow key={referral.id}>
                    <TableCell>{referral.referred || 'Usuario Desconocido'}</TableCell>
                    <TableCell>{referral.referrer || 'Desconocido'}</TableCell>
                    <TableCell>{referral.timestamp?.toDate?.().toLocaleDateString?.() || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        );

      case 'userRoleCounts':
        return (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ color: '#1976d2', mb: 2 }}>
              Conteo de Usuarios por Rol
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 3, flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6">Total de Usuarios</Typography>
                <Typography variant="h4">{userRoleCounts.total}</Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6">Usuarios Oro</Typography>
                <Typography variant="h4">{userRoleCounts.oro}</Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6">Usuarios Normales</Typography>
                <Typography variant="h4">{userRoleCounts.normal}</Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6">Moderadores</Typography>
                <Typography variant="h4">{userRoleCounts.moderador}</Typography>
              </Box>
            </Box>
          </Box>
        );

      // NEW: historial de inversiones
      case 'investmentHistory':
        return (
          <Box>
            <Typography variant="h5" gutterBottom sx={{ color: '#1976d2', mb: 2 }}>
              Historial de Inversión (últimas 100)
            </Typography>

            {loadingInvestments ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Usuario</strong></TableCell>
                      <TableCell><strong>Lotería</strong></TableCell>
                      <TableCell align="right"><strong>Monto</strong></TableCell>
                      <TableCell><strong>Números</strong></TableCell>
                      <TableCell><strong>Inicio</strong></TableCell>
                      <TableCell><strong>Fin</strong></TableCell>
                      <TableCell align="center"><strong>Estado</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {investments.length ? (
                      investments.map((r) => (
                        <TableRow key={r.id} hover>
                          <TableCell>{r.userId}</TableCell>
                          <TableCell>{r.lottery}</TableCell>
                          <TableCell align="right">{formatCurrency(r.amount)}</TableCell>
                          <TableCell>
                            {r.selectedNums?.length ? r.selectedNums.join(', ') : '—'}
                          </TableCell>
                          <TableCell>{r.startMs ? formatDate(r.startMs) : '—'}</TableCell>
                          <TableCell>{r.endMs ? formatDate(r.endMs) : '—'}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={r.status || '—'}
                              size="small"
                              color={r.status === 'active' ? 'warning' : r.status === 'completed' ? 'success' : 'default'}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                          No hay inversiones registradas.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ textAlign: 'center', color: '#1976d2' }}>
        Dashboard
      </Typography>

      <Box sx={{ display: 'flex', gap: 3, mt: 4 }}>
        {/* Menú Lateral */}
        <Box sx={{ width: '20%', backgroundColor: '#f9f9f9', borderRadius: '12px', p: 2, boxShadow: 3 }}>
          <List>
            <ListItem disablePadding>
              <ListItemButton
                selected={selectedMenu === 'realTimeSummary'}
                onClick={() => setSelectedMenu('realTimeSummary')}
              >
                <ListItemText primary="Resumen en Tiempo Real" />
              </ListItemButton>
            </ListItem>
            <Divider />
            <ListItem disablePadding>
              <ListItemButton
                selected={selectedMenu === 'topUsers'}
                onClick={() => setSelectedMenu('topUsers')}
              >
                <ListItemText primary="Monedas por Usuarios" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                selected={selectedMenu === 'vipDetails'}
                onClick={() => setSelectedMenu('vipDetails')}
              >
                <ListItemText primary="Costo VIP" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                selected={selectedMenu === 'recentUsers'}
                onClick={() => setSelectedMenu('recentUsers')}
              >
                <ListItemText primary="Últimos Usuarios Registrados" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                selected={selectedMenu === 'userRoleCounts'}
                onClick={() => setSelectedMenu('userRoleCounts')}
              >
                <ListItemText primary="Conteo de Usuarios por Rol" />
              </ListItemButton>
            </ListItem>
            <Divider />

            <ListItem disablePadding>
              <ListItemButton
                selected={selectedMenu === 'recentReferrals'}
                onClick={() => setSelectedMenu('recentReferrals')}
              >
                <ListItemText primary="Últimos Usuarios Referidos" />
              </ListItemButton>
            </ListItem>

            {/* NEW: opción Historial */}
            <Divider sx={{ my: 1 }} />
            <ListItem disablePadding>
              <ListItemButton
                selected={selectedMenu === 'investmentHistory'}
                onClick={() => setSelectedMenu('investmentHistory')}
              >
                <ListItemText primary="Historial de Inversión" />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>

        {/* Contenido Central */}
        <Box sx={{ flexGrow: 1, backgroundColor: '#fff', borderRadius: '12px', p: 4, boxShadow: 3 }}>
          {renderContent()}
        </Box>
      </Box>
    </Container>
  );
}

export default Dashboard;
